import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useEffect, useMemo, useState } from 'react';
import { slugify } from '@/lib/slugify';
import { getLocationSlugPaths } from '@/lib/locationStaticPaths';

const PAGE_SLUG = 'tow-truck-near-me';
const DEFAULT_CATEGORY_SLUG = 'tow-truck';
const CATEGORY_ID = '1772';
const BRAND_LABEL = 'Tow Truck';
const BRAND_LABEL_PLURAL = 'Tow Trucks';

const ALL_PROVINCE_SLUGS = [
    'gauteng', 'western-cape', 'kwazulu-natal', 'eastern-cape',
    'free-state', 'mpumalanga', 'limpopo', 'north-west', 'northern-cape',
];

const PROVINCE_DISPLAY_NAMES: Record<string, string> = {
    'gauteng': 'Gauteng', 'western-cape': 'Western Cape', 'kwazulu-natal': 'KwaZulu-Natal',
    'eastern-cape': 'Eastern Cape', 'free-state': 'Free State', 'mpumalanga': 'Mpumalanga',
    'limpopo': 'Limpopo', 'north-west': 'North West', 'northern-cape': 'Northern Cape',
};

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const quickProvinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Mpumalanga', 'Limpopo', 'North West', 'Northern Cape',
];

function toTitleCase(slug: string): string {
    return slug.split('-').filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

export default function TowTruckSlugPage() {
    const router = useRouter();

    // In a static export the shell HTML's __NEXT_DATA__ always contains
    // query: { slug: ['sss-shell'] }. Derive real path segments from router.asPath
    // which Next.js reconciles to the actual browser URL after hydration.
    // `mounted` ensures the initial client render matches the server render (both use empty slug)
    // so React does not throw a hydration mismatch error.
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const BASE_SLUG_PATH = `/${PAGE_SLUG}/`;
    const rawSlug: string[] = useMemo(() => {
        if (!mounted || !router.isReady) return [];
        const path = router.asPath.split('?')[0].replace(/\/$/, '');
        if (path.startsWith(BASE_SLUG_PATH)) {
            const segs = path.slice(BASE_SLUG_PATH.length).split('/').filter(Boolean);
            if (segs.length > 0 && segs[0] !== 'sss-shell') return segs;
        }
        const querySlug = (router.query.slug as string[] | undefined) ?? [];
        return querySlug.filter((s) => s !== 'sss-shell');
    }, [mounted, router.isReady, router.asPath, router.query.slug]);

    // URL format: /[page-slug]/[province]/[city?]/[suburb?]
    const [provinceSlug = '', citySlug = '', suburbSlug = ''] = rawSlug;

    const provinceName = PROVINCE_DISPLAY_NAMES[provinceSlug] ?? (provinceSlug ? toTitleCase(provinceSlug) : '');
    const cityName = citySlug ? toTitleCase(citySlug) : '';
    const suburbName = suburbSlug ? toTitleCase(suburbSlug) : '';
    const hasLocation = Boolean(provinceSlug || citySlug || suburbSlug);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;
    const locationSuffix = hasLocation ? `in ${locationLabel}` : 'in South Africa';
    const keywordList = [
        `${BRAND_LABEL_PLURAL.toLowerCase()} ${locationLabel || 'south africa'}`,
        `${BRAND_LABEL_PLURAL.toLowerCase()} near me`,
        `${BRAND_LABEL.toLowerCase()} near me`,
    ].join(', ');

    const pageTitle = hasLocation
        ? `Tow Trucks ${locationSuffix} | Tow Truck Near Me`
        : 'Tow Trucks in South Africa | Tow Truck Near Me';

    const pageDescription = hasLocation
        ? `Find tow trucks and roadside assistance ${locationSuffix}. Browse listings for breakdown recovery, vehicle towing, and emergency roadside help near you.`
        : 'Find tow trucks and roadside assistance across South Africa. Locate towing services, breakdown recovery, and emergency help near you by province or city.';

    const canonicalParts = ['', PAGE_SLUG];
    if (provinceSlug) canonicalParts.push(provinceSlug);
    if (citySlug) canonicalParts.push(citySlug);
    if (suburbSlug) canonicalParts.push(suburbSlug);
    const canonicalUrl = `${ENV.DOMAIN_URL}${canonicalParts.join('/')}`;
    const baseSlugPath = `/${PAGE_SLUG}`;

    const searchParams = { category: '', province: provinceSlug, city: citySlug, suburb: suburbSlug, text: '', page: 1 };

    const faqItems = [
        {
            question: `How do I find a tow truck ${locationSuffix}?`,
            answer: hasLocation
                ? `Browse tow truck and roadside assistance listings ${locationSuffix} using the filters above.`
                : 'Use province and city filters to find tow trucks near you. Most towing companies operate 24/7 for emergency assistance.',
        },
        {
            question: 'What services do tow truck companies offer?',
            answer: 'Tow truck services include vehicle recovery and towing, jumpstarts, flat tyre assistance, fuel delivery, lockout service, accident recovery, and long-distance towing.',
        },
        {
            question: 'Are tow truck services available 24/7?',
            answer: 'Most towing companies offer 24-hour emergency call-out services. Contact the listing directly to confirm availability and response times in your area.',
        },
        {
            question: 'How much does a tow truck cost?',
            answer: 'Towing rates vary by distance, vehicle type, and time of day. Emergency call-outs may carry a higher rate. Contact listings directly for quotes.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'BreadcrumbList',
                                    itemListElement: [
                                        { '@type': 'ListItem', position: 1, name: 'Tow Truck Near Me', item: `${ENV.DOMAIN_URL}/${PAGE_SLUG}` },
                                        ...(provinceSlug ? [{ '@type': 'ListItem', position: 2, name: provinceName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}` }] : []),
                                        ...(citySlug ? [{ '@type': 'ListItem', position: 3, name: cityName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}/${citySlug}` }] : []),
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: { '@type': 'Answer', text: item.answer },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>

            <section className="relative overflow-hidden bg-[#0a0a1a] text-white">
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            {hasLocation && (
                                <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm text-blue-200">
                                    <Link href={`/${PAGE_SLUG}`} className="hover:text-white">Tow Truck Near Me</Link>
                                    {provinceSlug && (<><span>/</span><Link href={`${baseSlugPath}/${provinceSlug}`} className="hover:text-white">{provinceName}</Link></>)}
                                    {citySlug && (<><span>/</span><span className="text-white">{cityName}</span></>)}
                                </nav>
                            )}
                            {!hasLocation && <p className="mb-3 text-xs uppercase tracking-[0.3em] text-blue-200">Roadside Assistance Directory</p>}
                            <h1 className="text-3xl font-bold leading-tight md:text-4xl">Tow Trucks {locationSuffix}</h1>
                            <p className="mt-4 text-base text-blue-100 md:text-lg">
                                Find towing companies and roadside assistance services near you. Available 24/7 for breakdowns, accidents, and vehicle recovery.
                            </p>
                            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                <Link href={`${baseSlugPath}/gauteng/johannesburg`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-blue-300">Tow Trucks Johannesburg</Link>
                                <Link href={`${baseSlugPath}/western-cape/cape-town`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-blue-300">Tow Trucks Cape Town</Link>
                                <Link href={`${baseSlugPath}/kwazulu-natal/durban`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-blue-300">Tow Trucks Durban</Link>
                                <Link href={`${baseSlugPath}/gauteng/pretoria`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-blue-300">Tow Trucks Pretoria</Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                                <h2 className="text-lg font-semibold">Tow Trucks by Province</h2>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {quickProvinces.map((p) => (
                                        <Link key={p} href={`${baseSlugPath}/${slugify(p)}`} className="rounded-full border border-white/20 px-3 py-1.5 text-sm hover:border-blue-300 hover:text-blue-200">{p}</Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds={CATEGORY_ID}
                        initialBusinesses={[]}
                        initialPagination={{ count: 0, currentPage: 1, totalPages: 0 }}
                    />
                </div>
            </div>

            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">Tow Trucks by City</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {quickCities.map((city) => (
                            <Link key={city.name} href={`${baseSlugPath}/${city.province}/${slugify(city.name)}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:border-blue-400 hover:text-blue-700">
                                {`Tow Trucks in ${city.name}`}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">Frequently Asked Questions</h2>
                    <div className="mt-4 space-y-4">
                        {faqItems.map((item) => (
                            <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <DownloadApp />
        </>
    );
}

export const getStaticPaths: GetStaticPaths = () => ({
    paths: [
        { params: { slug: ['sss-shell'] } },
        ...getLocationSlugPaths(3).map((slug) => ({ params: { slug } })),
    ],
    // false in production (static-export requirement); every province/city/suburb
    // is pre-generated above so no runtime fallback is needed.
    // blocking in dev so dynamic paths resolve during local development.
    fallback: process.env.NODE_ENV === 'production' ? false : 'blocking',
});

export const getStaticProps: GetStaticProps = async () => {
    return { props: {} };
};
